import { Skeleton } from '@/components/ui/Skeleton';

/**
 * A rail-shaped placeholder with the **same box** as a real rail: heading line,
 * subtitle line, "See all" link, then card frames at `HomeRailItem`'s width with
 * `ListingCard`'s aspect ratio and text rhythm.
 *
 * It exists because the personalized rails can only be fetched after the session
 * resolves, and the alternative to a placeholder is a void where the buyer's own
 * homes are about to be. Matching the real geometry is the point: a shorter
 * placeholder buys a layout shift when the cards land, and this project has
 * already shipped a shimmer that never became an image (see `useListingPhotos`),
 * so the heights here are copied from the components they stand in for rather
 * than guessed. (W14 R133)
 */
export function RailSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <section className="py-6" aria-hidden>
      <div className="mx-auto flex max-w-7xl items-end justify-between gap-4 px-4">
        <div className="min-w-0">
          {/* h2 text-2xl (32px) / sm:text-3xl (36px) */}
          <Skeleton className="h-8 w-56 sm:h-9 sm:w-72" />
          {/* subtitle: mt-1 text-sm (20px) */}
          <Skeleton className="mt-1 h-5 w-40" />
        </div>
        <Skeleton className="h-5 w-16 shrink-0" />
      </div>

      <div className="mt-4 flex gap-4 overflow-hidden px-4 pb-2">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="w-[78vw] max-w-[320px] flex-shrink-0 sm:w-[300px]">
            <div className="overflow-hidden rounded-card border border-hairline bg-surface/80">
              <Skeleton className="aspect-[4/3] w-full rounded-none" />
              <div className="p-4">
                <Skeleton className="mb-2 h-7 w-1/2" />
                <Skeleton className="mb-2 h-6 w-4/5" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="mt-1 h-5 w-2/3" />
                <Skeleton className="mt-2 h-4 w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
